import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { fetchAgent } from '@/api/limited-links';
import { isUnauthorized } from '@/api/client';
import { PROXY_AUTH } from '@/api/config';
import { clearAgentKey, readAgentKey, storeAgentKey } from './agent-key';
import { AgentContext, type Session } from './agent-context';

function initialSession(): Session {
  // Access has already identified the user; the proxy decides which agent that is.
  if (PROXY_AUTH) return { status: 'restoring', key: '' };
  const key = readAgentKey();
  return key ? { status: 'restoring', key } : { status: 'signed-out' };
}

export function AgentProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session>(initialSession);

  const signOut = useCallback(() => {
    clearAgentKey();
    setSession({ status: 'signed-out' });
  }, []);

  const signIn = useCallback(async (key: string) => {
    const agent = await fetchAgent(key);
    storeAgentKey(key);
    setSession({ status: 'signed-in', key, agent });
    return agent;
  }, []);

  // One client for the provider's life. Query keys carry the agent key, so a new key never reads
  // another key's cache. A 401 from any query (a key rotated mid-session) signs the agent out
  // instead of erroring page by page.
  const [queryClient] = useState(
    () =>
      new QueryClient({
        queryCache: new QueryCache({
          onError: (error) => {
            if (isUnauthorized(error)) signOut();
          },
        }),
        defaultOptions: {
          queries: {
            retry: (failureCount, error) => !isUnauthorized(error) && failureCount < 1,
            refetchOnWindowFocus: false,
          },
        },
      }),
  );

  // Signing out drops everything fetched with the old key.
  useEffect(() => {
    if (session.status === 'signed-out') queryClient.clear();
  }, [session.status, queryClient]);

  // A remembered key is verified before anything renders behind it: a rotated key must land on
  // the sign-in page, not on a dashboard full of errors.
  useEffect(() => {
    if (session.status !== 'restoring') return;
    const { key } = session;
    let cancelled = false;
    fetchAgent(key)
      .then((agent) => {
        if (!cancelled) setSession({ status: 'signed-in', key, agent });
      })
      .catch(() => {
        if (!cancelled) signOut();
      });
    return () => {
      cancelled = true;
    };
  }, [session, signOut]);

  const value = useMemo(() => ({ session, signIn, signOut }), [session, signIn, signOut]);

  return (
    <AgentContext.Provider value={value}>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </AgentContext.Provider>
  );
}
